import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CheckboxWembii } from "../CheckboxWembii";
import "./ExportPreviewModal.scss";

const PREVIEW_ROW_LIMIT = 200;

export type ExportColumn = {
  key: string;
  label: string;
};

export type ExportScope = "all" | "selected";

export type ExportScopeConfig = {
  value: ExportScope;
  selectedCount: number;
  onChange: (scope: ExportScope) => void;
};

interface Props {
  isOpen: boolean;
  title: string;
  columns: ExportColumn[];
  rows: Record<string, unknown>[];
  onClose: () => void;
  onDownload: (visibleColumns: ExportColumn[]) => void;
  countMessageId: string;
  truncatedMessageId: string;
  emptyMessageId: string;
  downloadLabelId?: string;
  scope?: ExportScopeConfig;
  loading?: boolean;
}

const formatCellValue = (value: unknown): string => {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
};

export default function ExportPreviewModal({
  isOpen,
  title,
  columns,
  rows,
  onClose,
  onDownload,
  countMessageId,
  truncatedMessageId,
  emptyMessageId,
  downloadLabelId = "app.export.downloadExcel",
  scope,
  loading = false,
}: Props) {
  const intl = useIntl();
  const [visibleKeys, setVisibleKeys] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      setVisibleKeys(columns.map((column) => column.key));
    }
  }, [columns, isOpen]);

  const visibleColumns = useMemo(
    () => columns.filter((column) => visibleKeys.includes(column.key)),
    [columns, visibleKeys]
  );

  const previewRows = useMemo(
    () => rows.slice(0, PREVIEW_ROW_LIMIT),
    [rows]
  );

  const isTruncated = rows.length > PREVIEW_ROW_LIMIT;

  const toggleColumn = useCallback((key: string) => {
    setVisibleKeys((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key]
    );
  }, []);

  const selectAllColumns = useCallback(() => {
    setVisibleKeys(columns.map((column) => column.key));
  }, [columns]);

  const clearAllColumns = useCallback(() => {
    setVisibleKeys([]);
  }, []);

  const handleDownload = useCallback(() => {
    if (!visibleColumns.length) return;
    onDownload(visibleColumns);
  }, [onDownload, visibleColumns]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      size="xl"
      className="export-preview"
    >
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody>
        {scope && (
          <div className="export-preview__scope mb-3">
            <span className="export-preview__columns-title">
              {intl.formatMessage({ id: "app.export.scope.title" })}
            </span>
            <div className="export-preview__scope-options">
              <label className="export-preview__scope-option">
                <input
                  type="radio"
                  name="export-scope"
                  checked={scope.value === "all"}
                  onChange={() => scope.onChange("all")}
                  disabled={loading}
                />
                <span>
                  {intl.formatMessage({ id: "app.export.scope.all" })}
                </span>
              </label>
              <label
                className={`export-preview__scope-option${
                  scope.selectedCount === 0
                    ? " export-preview__scope-option--disabled"
                    : ""
                }`}
              >
                <input
                  type="radio"
                  name="export-scope"
                  checked={scope.value === "selected"}
                  onChange={() => scope.onChange("selected")}
                  disabled={loading || scope.selectedCount === 0}
                />
                <span>
                  {intl.formatMessage(
                    { id: "app.export.scope.selected" },
                    { count: scope.selectedCount }
                  )}
                </span>
              </label>
            </div>
            {scope.selectedCount === 0 && (
              <p className="export-preview__scope-hint mb-0">
                {intl.formatMessage({ id: "app.export.scope.noneSelectedHint" })}
              </p>
            )}
          </div>
        )}

        <p className="export-preview__summary text-muted mb-3">
          {loading
            ? intl.formatMessage({ id: "app.export.loading" })
            : isTruncated
              ? intl.formatMessage(
                  { id: truncatedMessageId },
                  {
                    shown: previewRows.length,
                    total: rows.length,
                  }
                )
              : intl.formatMessage(
                  { id: countMessageId },
                  { total: rows.length }
                )}
        </p>

        {columns.length > 0 && (
          <div className="export-preview__columns mb-3">
            <div className="export-preview__columns-head">
              <span className="export-preview__columns-title">
                {intl.formatMessage({ id: "app.export.columns" })}
              </span>
              <div className="export-preview__columns-actions">
                <button
                  type="button"
                  className="export-preview__link-btn"
                  onClick={selectAllColumns}
                >
                  {intl.formatMessage({ id: "app.export.columns.selectAll" })}
                </button>
                <button
                  type="button"
                  className="export-preview__link-btn"
                  onClick={clearAllColumns}
                >
                  {intl.formatMessage({ id: "app.export.columns.clearAll" })}
                </button>
              </div>
            </div>
            <div className="export-preview__columns-grid">
              {columns.map((column) => (
                <CheckboxWembii
                  key={column.key}
                  id={`export-col-${column.key}`}
                  mb={false}
                  checked={visibleKeys.includes(column.key)}
                  label={column.label}
                  onChange={() => toggleColumn(column.key)}
                />
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="export-preview__loading">
            <Spinner color="primary" />
          </div>
        ) : rows.length === 0 ? (
          <p className="mb-0">{intl.formatMessage({ id: emptyMessageId })}</p>
        ) : visibleColumns.length === 0 ? (
          <p className="mb-0 text-muted">
            {intl.formatMessage({ id: "app.export.columns.noneSelected" })}
          </p>
        ) : (
          <div className="export-preview__table-wrap">
            <Table bordered hover size="sm" className="mb-0">
              <thead>
                <tr>
                  {visibleColumns.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, index) => (
                  <tr key={(row.id as string) || index}>
                    {visibleColumns.map((column) => (
                      <td key={`${index}-${column.key}`}>
                        {formatCellValue(row[column.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose} disabled={loading}>
          {intl.formatMessage({ id: "app.socialMediaPost.cancel" })}
        </Button>
        <Button
          color="primary"
          onClick={handleDownload}
          disabled={loading || rows.length === 0 || visibleColumns.length === 0}
          className="d-flex align-items-center"
        >
          <FontAwesomeIcon
            icon={["fas", "file-excel"]}
            size="1x"
            className="mr-2"
          />
          {intl.formatMessage({ id: downloadLabelId })}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export function mapRowsToSheetData(
  rows: Record<string, unknown>[],
  columns: ExportColumn[]
): Record<string, string | number>[] {
  return rows.map((row) =>
    columns.reduce<Record<string, string | number>>((acc, column) => {
      const value = row[column.key];
      if (Array.isArray(value)) {
        acc[column.label] = value.join(", ");
      } else if (value == null) {
        acc[column.label] = "";
      } else {
        acc[column.label] = value as string | number;
      }
      return acc;
    }, {})
  );
}
